<script lang="ts">
  import { ListItem, Logo } from './components/header';
  import { getTextColor } from './helpers/sections-helper';
  import { Map } from './components/index.js';
  import { LocationIcon, VerifiedIcon } from './components/icons';
  import { SUBSCRIPTION_STATUSES } from './constants/subscription.js';
  import Availability from './Availability.svelte';
  import type {
    GlobalProperties,
    UseFormattedProps,
  } from 'customer-components/lib/types';

  export let properties: UseFormattedProps;
  export let settings: GlobalProperties;

  const sortCompanyInfoItems = () => {
    const sortedItems = [];

    const aboutUs = properties.links.items.find(
      item => item.label === 'About Us' || item.label === 'About Me',
    );
    if (aboutUs) sortedItems.push(aboutUs);

    const reviews = properties.links.items.find(
      item => item.label === 'Reviews',
    );
    if (reviews) sortedItems.push(reviews);

    const faq = properties.links.items.find(item => item.label === 'FAQ');
    if (faq) sortedItems.push(faq);

    return sortedItems;
  };

  const sortServiceItems = sortedCompanyInfo => {
    const services = properties.links.items.filter(
      item => !sortedCompanyInfo.includes(item),
    );

    return services;
  };

  const sortedCompanyInfo = sortCompanyInfoItems();
  const sortedServices = sortServiceItems(sortedCompanyInfo);

  const toggleSocials = props => {
    return (
      props.hideSocials &&
      (!!props.socials.facebook ||
        !!props.socials.twitter ||
        !!props.socials.instagram ||
        !!props.socials.linkedIn ||
        !!props.socials.youtube ||
        !!props.socials.tiktok)
    );
  };

  $: ({ generalSettings, brand } = settings);
  $: isSocialIconsActive = toggleSocials(properties);
  $: availability = generalSettings?.availability;
  $: center = {
    lat: properties.location.latitude,
    lng: properties.location.longitude,
  };
  $: radius = properties.location.radius;
  $: backgroundColor = brand?.palette.colors.find(
    color => color.type === 'background',
  )?.value;
  $: textColor = getTextColor(backgroundColor);
</script>

{#if properties.location.active && properties.location.position === 'top'}
  <div class="footer__map footer__map-top">
    <Map {center} {radius} googleApiLoad={properties.googleApiLoad} />
  </div>
{/if}

<footer>
  <div class="container">
    {#if brand?.default.active || brand?.custom.active}
      <div class="footer__company-logo">
        <Logo isFooter {brand} size={24} />

        {#if brand.text.active}
          <a href="/" class="company-info__logo-text">
            {brand.text.value}
          </a>
        {/if}
      </div>
    {/if}
    <div class="wrapper">
      {#if properties.location.active && properties.location.position === 'left'}
        <div class="footer__map footer__map-left">
          <Map {center} {radius} googleApiLoad={properties.googleApiLoad} />
        </div>
      {/if}
      <div class="business-information">
        {#if properties.description}
          <p class="description">
            {@html properties.description}
          </p>
        {/if}
        <p class="address">
          <span style="color:var(--vlt-lp-accent);"><LocationIcon /></span>
          {properties.location.fullAddress}
        </p>
        {#if properties.insuranceNumbers.active}
          <p class="license">
            <span style="color:var(--vlt-lp-accent);"><VerifiedIcon /></span>
            Licensed & Insured:
            {properties.insuranceNumbers.insurance}
            {properties.insuranceNumbers.insurance &&
              properties.insuranceNumbers.license &&
              '•'}
            {properties.insuranceNumbers.license}
          </p>
        {/if}
      </div>
      <div class="business-links">
        {#if generalSettings?.availabilityEnabled}
          <div class="links-section">
            <strong>Opening Hours</strong>
            <div class="company-availability">
              <Availability {availability} />
            </div>
          </div>
        {/if}
        <div class="links-section">
          <strong>Company Info</strong>
          <ul>
            {#each sortedCompanyInfo as link}
              <li class="footer-services__item">
                <ListItem
                  isFooter
                  url={link.url}
                  label={link.label}
                  type={link.type}
                />
              </li>
            {/each}
          </ul>
        </div>
        <div class="links-section links-section--services">
          <strong>Our Services</strong>
          <ul>
            {#each sortedServices as link}
              <li class="footer-services__item">
                <ListItem
                  isFooter
                  url={link.url}
                  label={link.label}
                  type={link.type}
                />
              </li>
            {/each}
          </ul>
        </div>
      </div>
      {#if properties.location.active && properties.location.position === 'right'}
        <div class="footer__map footer__map-right">
          <Map {center} {radius} googleApiLoad={properties.googleApiLoad} />
        </div>
      {/if}
    </div>
    <div class="footer-notes">
      <span style="opacity:.5;font-weight:400;"
        >© Copyright {properties.businessName.value}
        {new Date().getFullYear()}. All rights reserved</span
      >
      <a
        class="footer-resources__terms-item footer-resources__terms-item--sitemap"
        target="_blank"
        rel="noreferrer"
        href="/sitemap.xml">Sitemap</a
      >
      {#if isSocialIconsActive}
        <div
          class="footer-socials"
          style="color:var(--vlt-lp-background-text);"
        >
          {#if properties.socials.facebook}
            <a
              href={properties.socials.facebook}
              target="_blank"
              aria-label="Facebook"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#8D939A" />
                <path
                  d="M12.803 13.0342V18.3533H10.3595V13.0342H8.32959V10.8774H10.3595V10.0926C10.3595 7.17931 11.5765 5.64746 14.1516 5.64746C14.941 5.64746 15.1383 5.77433 15.5706 5.87771V8.01102C15.0866 7.92644 14.9504 7.87945 14.4476 7.87945C13.8508 7.87945 13.5313 8.04861 13.24 8.38223C12.9486 8.71586 12.803 9.29382 12.803 10.1208V10.8821H15.5706L14.8282 13.0389H12.803V13.0342Z"
                  fill="white"
                />
              </svg>
            </a>
          {/if}

          {#if properties.socials.linkedIn}
            <a
              href={properties.socials.linkedIn}
              target="_blank"
              aria-label="LinkedIn"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#8D939A" />
                <path
                  d="M9.18614 9.50134H6.82617V17.0271H9.18614V9.50134Z"
                  fill="white"
                />
                <path
                  d="M15.57 9.33829C15.483 9.32741 15.3906 9.32197 15.2981 9.31654C13.9768 9.26216 13.2318 10.0452 12.9708 10.3823C12.9001 10.4748 12.8675 10.5291 12.8675 10.5291V9.52317H10.6108V17.049H12.8675H12.9708C12.9708 16.2822 12.9708 15.521 12.9708 14.7542C12.9708 14.341 12.9708 13.9277 12.9708 13.5144C12.9708 13.0033 12.9327 12.4595 13.1883 11.9919C13.4058 11.6004 13.7973 11.4046 14.2378 11.4046C15.5428 11.4046 15.57 12.5846 15.57 12.6934C15.57 12.6988 15.57 12.7042 15.57 12.7042V17.0816H17.93V12.1713C17.93 10.4911 17.0763 9.50142 15.57 9.33829Z"
                  fill="white"
                />
                <path
                  d="M8.00555 8.52967C8.76235 8.52967 9.37586 7.91616 9.37586 7.15937C9.37586 6.40257 8.76235 5.78906 8.00555 5.78906C7.24876 5.78906 6.63525 6.40257 6.63525 7.15937C6.63525 7.91616 7.24876 8.52967 8.00555 8.52967Z"
                  fill="white"
                />
              </svg>
            </a>
          {/if}

          {#if properties.socials.instagram}
            <a
              href={properties.socials.instagram}
              target="_blank"
              aria-label="Instagram"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#8D939A" />
                <path
                  d="M15.2896 16.9401H8.71088C7.80178 16.9401 7.05908 16.1974 7.05908 15.2883V8.70953C7.05908 7.80044 7.80178 7.05774 8.71088 7.05774H15.2896C16.1987 7.05774 16.9414 7.80044 16.9414 8.70953V15.2883C16.9414 16.2015 16.2028 16.9401 15.2896 16.9401Z"
                  fill="white"
                />
                <path
                  d="M12.0019 14.5389C11.3241 14.5389 10.6869 14.2751 10.208 13.7962C9.72913 13.3173 9.46533 12.6801 9.46533 12.0024C9.46533 11.3246 9.72913 10.6874 10.208 10.2085C10.6869 9.72962 11.3241 9.46582 12.0019 9.46582C12.6796 9.46582 13.3168 9.72962 13.7957 10.2085C14.2746 10.6874 14.5384 11.3246 14.5384 12.0024C14.5384 12.6801 14.2746 13.3173 13.7957 13.7962C13.3128 14.2751 12.6796 14.5389 12.0019 14.5389ZM12.0019 10.0056C10.902 10.0056 10.0051 10.8985 10.0051 12.0024C10.0051 13.1022 10.898 13.9991 12.0019 13.9991C13.1017 13.9991 13.9986 13.1063 13.9986 12.0024C13.9946 10.9025 13.1017 10.0056 12.0019 10.0056Z"
                  fill="#8D939A"
                />
                <path
                  d="M15.0324 9.38214C15.3014 9.38214 15.5195 9.16409 15.5195 8.89511C15.5195 8.62613 15.3014 8.40808 15.0324 8.40808C14.7635 8.40808 14.5454 8.62613 14.5454 8.89511C14.5454 9.16409 14.7635 9.38214 15.0324 9.38214Z"
                  fill="#8D939A"
                />
              </svg>
            </a>
          {/if}

          {#if properties.socials.twitter}
            <a
              href={properties.socials.twitter}
              target="_blank"
              aria-label="Twitter"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#8D939A" />
                <path
                  d="M6.77588 15.6653C7.99486 15.7339 9.0608 15.4489 10.1004 14.6943C9.05025 14.5149 8.32203 14.0347 7.92626 13.0426C8.23232 13.0004 8.50145 13.0796 8.81806 12.9529C7.78378 12.4991 7.11361 11.8184 7.08722 10.6416C7.41439 10.668 7.66241 10.8843 8.05291 10.8527C7.10305 9.92394 6.81282 8.91076 7.40911 7.68123C8.40118 8.82105 9.56212 9.59676 10.9816 9.96088C11.0608 9.98198 11.1346 10.0031 11.2138 10.0189C11.5779 10.1034 12.0106 10.2828 12.2323 10.2564C12.6122 10.2089 12.2323 9.7709 12.3272 9.20099C12.628 7.42794 14.5805 6.62056 16.0264 7.66012C16.4485 7.96618 16.781 7.96091 17.1979 7.77621C17.4142 7.68123 17.6359 7.58624 17.8944 7.47543C17.8364 7.98202 17.4459 8.26169 17.1345 8.63636C17.4881 8.71551 17.7731 8.61525 18.1002 8.50971C17.9894 8.87382 17.715 9.07962 17.467 9.28015C17.2084 9.48595 17.1082 9.7023 17.0976 10.04C16.9393 15.1534 11.1294 19.1058 7.15582 15.9608C6.77061 15.6547 7.14527 15.9608 6.77588 15.6653Z"
                  fill="white"
                />
              </svg>
            </a>
          {/if}

          {#if properties.socials.youtube}
            <a
              href={properties.socials.youtube}
              target="_blank"
              aria-label="YouTube"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#8D939A" />
                <path
                  d="M16.5839 16.517H7.54789C6.33884 16.517 5.36475 15.4477 5.36475 14.1325V9.86615C5.36475 8.54561 6.34374 7.48169 7.54789 7.48169H16.5839C17.793 7.48169 18.7671 8.55095 18.7671 9.86615V14.1325C18.772 15.4531 17.793 16.517 16.5839 16.517Z"
                  fill="white"
                />
                <path
                  d="M14.436 11.9331L10.6348 9.74109V14.1251L14.436 11.9331Z"
                  fill="#8D939A"
                />
              </svg>
            </a>
          {/if}

          {#if properties.socials.tiktok}
            <a
              href={properties.socials.tiktok}
              target="_blank"
              aria-label="TikTok"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#8D939A" />
                <path
                  d="M16.4276 8.90246C15.8461 8.77459 15.3201 8.4658 14.925 8.0204C14.5299 7.575 14.286 7.01588 14.2284 6.42329V6.16663H12.2159V14.1408C12.2163 14.4946 12.1054 14.8396 11.8988 15.1269C11.6923 15.4142 11.4006 15.6292 11.065 15.7414C10.7295 15.8537 10.3671 15.8576 10.0293 15.7525C9.69143 15.6474 9.39521 15.4386 9.18258 15.1558C8.9921 14.9054 8.87545 14.6068 8.84584 14.2936C8.81623 13.9804 8.87484 13.6652 9.01504 13.3836C9.15524 13.102 9.37143 12.8653 9.63917 12.7001C9.90691 12.535 10.2155 12.448 10.5301 12.4491C10.7041 12.4481 10.8772 12.4737 11.0434 12.525V10.4833C10.8499 10.4593 10.655 10.4496 10.4601 10.4541C9.74218 10.4733 9.04545 10.7014 8.45522 11.1105C7.86498 11.5196 7.40685 12.092 7.13694 12.7575C6.86702 13.423 6.79703 14.1528 6.93554 14.8575C7.07404 15.5621 7.41503 16.2111 7.91675 16.725C8.43095 17.2477 9.08896 17.6059 9.80716 17.7538C10.5254 17.9018 11.2713 17.8329 11.9503 17.556C12.6293 17.279 13.2106 16.8065 13.6204 16.1984C14.0302 15.5903 14.2499 14.8741 14.2518 14.1408V10.0575C15.0634 10.6373 16.0368 10.9474 17.0343 10.9441V8.96079C16.8383 8.9616 16.6428 8.94205 16.4509 8.90246H16.4276Z"
                  fill="white"
                />
              </svg>
            </a>
          {/if}
        </div>
      {/if}
      <div class="footer-notes-links">
        <a
          target="_blank"
          rel="noreferrer"
          class="whitespace-nowrap"
          href={properties.privacyLink.url}>{properties.privacyLink.label}</a
        >

        <a
          target="_blank"
          rel="noreferrer"
          class="whitespace-nowrap"
          href={properties.termLink.url}>{properties.termLink.label}</a
        >
      </div>

      {#if properties.subscriptionsStatus !== SUBSCRIPTION_STATUSES.ACTIVE}
        <img
          class="footer-resources__logo"
          style="height:19px;"
          height="19"
          width="125"
          loading="lazy"
          src={textColor != '#0E0F11'
            ? 'https://voolt-template-asset.s3.amazonaws.com/voolt-logo-light.png'
            : 'https://voolt-template-asset.s3.amazonaws.com/voolt-logo-dark.png'}
          alt="logo"
        />
      {/if}
    </div>
  </div>
</footer>

<style lang="scss">
  :root {
    --spacing-large-lg: 40px;
    --spacing-micro: 12px;
    --spacing-middle-lg: 20px;
    --spacing-middle-md: 16px;
    --spacing-middle-xl: 24px;
    --spacing-mini: 16px;
    --spacing-quarck: 2px;
    --spacing-small-xl: 8px;
    --spacing-small: 20px;
  }
  .company-info__logo-text {
    word-break: break-word;
  }
  .footer__map {
    display: block;
    width: 100%;
    &-top {
      height: 320px;
    }
    &-left {
      margin-right: 16px;
    }
    &-right {
      margin-left: 16px;
    }
    &-left,
    &-right {
      height: 124px;
      width: 124px;
    }
  }
  footer {
    padding: 80px 15px;
    background-color: var(--vlt-lp-background, #ffffff);
    .footer__company-logo {
      display: flex;
      grid-gap: 8px;
      margin-bottom: 16px;
      align-items: center;
      a {
        color: var(--vlt-lp-background-text, #202123);
        font-family: var(--vlt-lp-heading-font);
        font-size: 22px;
        font-style: normal;
        font-weight: 900;
        line-height: 135%;
        letter-spacing: -0.11px;
        text-decoration: none;
        text-transform: uppercase;
      }
    }
    .container {
      width: 100%;
      max-width: 1240px;
      margin: 0 auto;
    }
    .wrapper {
      display: flex;
      .business-information {
        flex: 1;
        .description {
          width: 100%;
          max-width: 400px;
          color: var(--vlt-lp-background-text, #565b61);
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 150%;
          letter-spacing: 0.14px;
          margin: 0;
          margin-bottom: 32px;
          opacity: 0.7;
        }
        .address {
          display: flex;
          grid-gap: 8px;
          color: var(--vlt-lp-background-text, #202123);
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: 20px;
          letter-spacing: 0.14px;
        }
        .license {
          display: flex;
          grid-gap: 8px;
          color: var(--vlt-lp-background-text, #202123);
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: 20px;
          letter-spacing: 0.14px;
        }
      }
      .business-links {
        display: flex;
        grid-gap: 36px;
        .links-section {
          strong {
            color: var(--vlt-lp-background-text, #202123);
            font-size: 14px;
            font-style: normal;
            font-weight: 600;
            line-height: 20px; /* 142.857% */
            letter-spacing: 0.14px;
          }
          ul {
            display: flex;
            grid-gap: 12px;
            flex-direction: column;
            list-style-type: none;
            padding: 0;
            margin: 20px 0 0 0;
            li {
              color: var(--vlt-lp-background-text, #202123);
              font-size: 14px;
              font-style: normal;
              font-weight: 400;
              line-height: 150%; /* 21px */
              letter-spacing: 0.14px;
              padding: 0;
              margin: 0;
              width: 150px;
            }
          }
          &--services {
            ul {
              display: grid;
              column-gap: 36px;
              grid-template-columns: repeat(2, 150px);
            }
          }
        }
      }
    }
    .footer-notes {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid #ecedef;
      margin-top: 40px;
      padding-top: 40px;
      color: var(--vlt-lp-background-text, #202123);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      letter-spacing: 0.14px;
      grid-gap: 12px;
      a {
        color: var(--vlt-lp-background-text, #202123);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        letter-spacing: 0.14px;
        text-decoration: none;
      }

      .footer-socials {
        display: flex;
        flex: 1;
        align-items: center;
        justify-content: center;
        grid-gap: 12px;
      }
      .footer-notes-links {
        display: flex;
        grid-gap: 24px;
      }
    }
  }
  @media screen and (max-width: 1060px) {
    .wrapper {
      flex-direction: column;
    }

    .footer__map {
      display: block;
      width: 100%;
      &-top {
        height: 320px;
      }
      &-left,
      &-right {
        height: 124px;
        margin: 16px 0 0 0;
      }
    }
  }
  @media screen and (max-width: 767px) {
    .footer__map {
      height: 150px;
    }
    footer {
      padding: 40px 15px;
      padding-bottom: 0;
      .wrapper {
        flex-direction: column;
        .business-information {
          padding-bottom: 24px;
          margin-bottom: 24px;
          border-bottom: 1px solid #ecedef;
          .description {
            margin-bottom: 24px;
          }
        }
        .business-links {
          display: flex;
          flex-wrap: wrap;
          .links-section {
            flex: 1 1 150px;
          }
          .links-section--services {
            flex: 1 1 100%;
            ul {
              display: flex;
            }
          }
        }
      }
      .footer-notes {
        flex-direction: column;

        :nth-child(1) {
          order: 5;
        }
        :nth-child(2) {
          order: 3;
        }
        :nth-child(3) {
          order: 1;
        }
        :nth-child(4) {
          order: 4;
        }
        :nth-child(5) {
          order: 6;
        }
      }
    }

    .footer__map {
      display: block;
      width: 100%;
      &-top {
        height: 320px;
      }
      &-left,
      &-right {
        height: 124px;
        margin: 16px 0 0 0;
      }
    }
  }
  @media screen and (max-width: 480px) {
    .footer-notes {
      padding-bottom: 80px;
    }
  }
</style>
